import { BloodpanelGroup, BloodpanelReport, BloodpanelReportValue } from "../../network/APITypes";
import {
    BLOODPANEL_GROUP_ORDER,
    BLOODPANEL_SORT_ENTRIES,
    OTHER_VALUES_GROUP_KEY,
} from "./bloodpanelSortConfig";

export { OTHER_VALUES_GROUP_KEY } from "./bloodpanelSortConfig";

type ResolvedSortConfig = {
    sortIndex: number;
    group: string;
    excluded?: boolean;
    unit?: string;
};

const normalizeUnit = (unit?: string) => unit?.trim().toLowerCase() ?? "";

const normalizeName = (name?: string) => name?.trim().toLowerCase() ?? "";

const normalizeShortName = (shortName?: string) => shortName?.trim().toUpperCase() ?? "";

const toValueKey = (name?: string, unit?: string) => `${normalizeName(name)}_${normalizeUnit(unit)}`;

const DIFFERENTIAL_BASE_NAMES = [
    "Stabkernige",
    "Segmentkernige",
    "Eosinophile",
    "Basophile",
    "Monozyten",
    "Lymphozyten",
    "Unreife Granulozyten",
];

/** Whether `name` is one of the differential blood count parameters that report a %
 * and a G/l (absolute) value under the SAME name — these must never be merged into one row. */
export const isDifferentialBaseName = (name?: string) =>
    DIFFERENTIAL_BASE_NAMES.some((baseName) => normalizeName(baseName) === normalizeName(name));

const matchesEntryUnit = (entryUnit: string | undefined, valueUnit?: string) => {
    const normalizedEntryUnit = normalizeUnit(entryUnit);
    const normalizedValueUnit = normalizeUnit(valueUnit);

    if (normalizedEntryUnit === normalizedValueUnit) {
        return true;
    }

    if (!normalizedEntryUnit && !normalizedValueUnit) {
        return true;
    }

    return false;
};

const resolveMapByNameUnit = new Map<string, ResolvedSortConfig>();
const resolveMapByShortName = new Map<string, ResolvedSortConfig>();
const resolveMapByLabel = new Map<string, ResolvedSortConfig[]>();

BLOODPANEL_SORT_ENTRIES.forEach((entry) => {
    const resolved: ResolvedSortConfig = {
        sortIndex: entry.sortIndex,
        group: entry.group,
        excluded: entry.excluded,
        unit: entry.unit,
    };

    const registerNameUnit = (name: string, unit?: string) => {
        resolveMapByNameUnit.set(toValueKey(name, unit), resolved);
    };

    const registerLabel = (label: string) => {
        const key = normalizeName(label);
        const existing = resolveMapByLabel.get(key) ?? [];
        existing.push(resolved);
        resolveMapByLabel.set(key, existing);
    };

    registerNameUnit(entry.name, entry.unit);
    entry.aliases?.forEach((alias) => registerNameUnit(alias, entry.unit));

    if (entry.shortName) {
        resolveMapByShortName.set(normalizeShortName(entry.shortName), resolved);
    }

    if (entry.label) {
        registerLabel(entry.label);
    }

    registerLabel(entry.name);
});

const pickByUnit = (candidates: ResolvedSortConfig[], unit?: string) => {
    const exact = candidates.filter((candidate) => matchesEntryUnit(candidate.unit, unit));
    return exact[0] ?? candidates[0];
};

const resolveSortConfig = (
    name?: string,
    unit?: string,
    shortName?: string,
): ResolvedSortConfig | undefined => {
    if (shortName) {
        const byShortName = resolveMapByShortName.get(normalizeShortName(shortName));
        if (byShortName) {
            return byShortName;
        }
    }

    const byNameUnit = resolveMapByNameUnit.get(toValueKey(name, unit));
    if (byNameUnit) {
        return byNameUnit;
    }

    if (name) {
        const byLabel = resolveMapByLabel.get(normalizeName(name));
        if (byLabel?.length) {
            return pickByUnit(byLabel, unit);
        }
    }

    return undefined;
};

/** Target group from Befundparameter mapping; undefined if not in config. */
export const getBloodpanelTargetGroup = (
    name?: string,
    unit?: string,
    shortName?: string,
): string | undefined => {
    const group = resolveSortConfig(name, unit, shortName)?.group;

    if (!group) {
        return undefined;
    }

    // Remap legacy / non-suggested groups to the groups from "Vorgeschlagene Gruppierung".
    // Everything else that isn't part of the suggested groups goes to "Weitere Werte".
    switch (group) {
        case "Hämatologie":
            return "Blutbild";
        case "Eisenstoffwechsel":
            return "Eisen";
        case "Harnbefund":
        case "Harnsediment":
            return "Harn";
        case "Schilddrüsenparameter":
            return "Hormone";
        case "Gesamteiweiß":
            return OTHER_VALUES_GROUP_KEY;
        case "Pankreas":
            return "Bauchspeicheldrüsewerte";
        default:
            break;
    }

    // Only allow the suggested groups; everything else becomes "Weitere Werte".
    if (BLOODPANEL_GROUP_ORDER.includes(group)) {
        return group;
    }

    return OTHER_VALUES_GROUP_KEY;
};

/** Hides configured exclusions and G/l differential rows without "abs." in the name. */
export const shouldShowBloodpanelValue = (
    name?: string,
    unit?: string,
    shortName?: string,
): boolean => {
    const resolved = resolveSortConfig(name, unit, shortName);

    if (resolved?.excluded) {
        return false;
    }

    if (normalizeUnit(unit) === "g/l" && isDifferentialBaseName(name) && !normalizeName(name).endsWith(" abs.")) {
        return false;
    }

    return true;
};

export const getBloodpanelSortIndex = (name?: string, unit?: string, shortName?: string): number =>
    resolveSortConfig(name, unit, shortName)?.sortIndex ?? Number.MAX_SAFE_INTEGER;

const compareAlphabetically = (
    a: Pick<BloodpanelReportValue, "name" | "unit">,
    b: Pick<BloodpanelReportValue, "name" | "unit">,
): number => {
    const nameCompare = (a.name ?? "").localeCompare(b.name ?? "", "de");
    if (nameCompare !== 0) {
        return nameCompare;
    }

    return normalizeUnit(a.unit).localeCompare(normalizeUnit(b.unit), "de");
};

export const compareBloodpanelValues = (
    a: Pick<BloodpanelReportValue, "name" | "unit" | "shortName">,
    b: Pick<BloodpanelReportValue, "name" | "unit" | "shortName">,
): number => {
    const indexA = getBloodpanelSortIndex(a.name, a.unit, a.shortName);
    const indexB = getBloodpanelSortIndex(b.name, b.unit, b.shortName);

    if (indexA !== indexB) {
        return indexA - indexB;
    }

    return compareAlphabetically(a, b);
};

export const sortBloodpanelGroupNames = (groupNames: string[]): string[] =>
    [...groupNames].sort((a, b) => {
        if (a === OTHER_VALUES_GROUP_KEY) {
            return 1;
        }
        if (b === OTHER_VALUES_GROUP_KEY) {
            return -1;
        }

        const indexA = BLOODPANEL_GROUP_ORDER.indexOf(a);
        const indexB = BLOODPANEL_GROUP_ORDER.indexOf(b);

        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
        }
        if (indexA !== -1) {
            return -1;
        }
        if (indexB !== -1) {
            return 1;
        }

        return a.localeCompare(b, "de");
    });

/** @deprecated Use sortBloodpanelGroupNames */
export const mergeBloodpanelGroupNames = sortBloodpanelGroupNames;

const sortValues = (values: BloodpanelReportValue[]) => [...values].sort(compareBloodpanelValues);

const addUniqueValue = (target: BloodpanelReportValue[], value: BloodpanelReportValue) => {
    const key = `${toValueKey(value.name, value.unit)}_${normalizeShortName(value.shortName)}`;

    if (
        !target.some(
            (item) => `${toValueKey(item.name, item.unit)}_${normalizeShortName(item.shortName)}` === key,
        )
    ) {
        target.push(value);
    }
};

const relocateGroups = (groups: BloodpanelGroup[]): BloodpanelGroup[] => {
    const valuesByGroup = new Map<string, BloodpanelReportValue[]>();

    groups.forEach((group) => {
        group.values?.forEach((value) => {
            if (!shouldShowBloodpanelValue(value.name, value.unit, value.shortName)) {
                return;
            }

            // Do not accept backend grouping. If we cannot map a value via our config,
            // it always belongs to "Weitere Werte".
            const targetGroup =
                getBloodpanelTargetGroup(value.name, value.unit, value.shortName) ?? OTHER_VALUES_GROUP_KEY;

            const existing = valuesByGroup.get(targetGroup) ?? [];
            addUniqueValue(existing, value);
            valuesByGroup.set(targetGroup, existing);
        });
    });

    const result: BloodpanelGroup[] = [];

    BLOODPANEL_GROUP_ORDER.forEach((groupName) => {
        const values = valuesByGroup.get(groupName);
        if (values?.length) {
            result.push({ name: groupName, values: sortValues(values) });
            valuesByGroup.delete(groupName);
        }
    });

    valuesByGroup.forEach((values, groupName) => {
        if (values.length) {
            result.push({ name: groupName, values: sortValues(values) });
        }
    });

    return result;
};

/** Filter, regroup per Befundparameter, sort values and panels for presentation. */
export const applyBloodpanelDisplayRules = (report: BloodpanelReport): BloodpanelReport => {
    if (!report.values?.length) {
        return report;
    }

    const relocated = relocateGroups(report.values);

    if (!relocated.length) {
        return { ...report, values: undefined };
    }

    return { ...report, values: relocated };
};
